import { FetchItemTypesArgs } from '../../Processes/Gateways/ItemTypes/Interfaces/FetchItemTypesArgs';
import { ItemEntity } from '@entities/Item/Types';
import { ItemModel } from '@entities/Item/ItemModel';
import { ItemTypeGateway } from '../../Processes/Gateways/ItemTypes/ItemTypesGateway';
import { useItemTypeStore } from '../../Stores/ItemTypeStore';
import { ItemTypeEntity } from '@entities/ItemType/Types';
import { useFragmentsStore } from '../../Stores/FragmentsStore';

const itemTypeGateway = new ItemTypeGateway();

export class ItemTypeViewModel {
    fetchItemType = async (args: FetchItemTypesArgs) => {
        const { updatedItems, setItemsData, setIsItemsLoading } =
            useItemTypeStore.getState();

        setIsItemsLoading(true);
        try {
            const response = await itemTypeGateway.getItemTypeItems(args);

            if (response.error || !response.data) {
                console.error(response.message);
                return { error: true, message: 'Failed to get products' };
            }

            const itemsData = Array.isArray(response.data.items.data)
                ? response.data.items.data
                : [response.data.items.data];

            const itemModels: ItemEntity[] = itemsData.map(
                item => new ItemModel(item)
            );

            if (args.itemType.items && args.itemType.items.length > 0) {
                itemModels.forEach(item =>
                    item.updateQuantityFromItems(args.itemType.items ?? [])
                );
            }

            if (updatedItems.length > 0) {
                itemModels.forEach(item =>
                    item.updateQuantityFromItems(updatedItems)
                );
            }

            const data = {
                items: itemModels,
                pagination: response.data.items.pagination,
            };

            setItemsData(data);
        } finally {
            setIsItemsLoading(false);
        }
    };

    updateItemTypeItems = (itemType: ItemTypeEntity) => {
        const { updatedItems, setUpdatedItems } = useItemTypeStore.getState();
        const { parsedFragments, setParsedFragments } =
            useFragmentsStore.getState();

        if (updatedItems.length < 1) {
            return;
        }

        const filteredItems = updatedItems.filter(
            item => item.qty && item.qty > 0
        );
        const updatedItemsIds = updatedItems.map(item => item.id);

        const filteredItemTypesItems =
            itemType.items?.filter(
                item => !updatedItemsIds.includes(item.id)
            ) ?? [];

        itemType.updateItems([...filteredItems, ...filteredItemTypesItems]);

        setUpdatedItems([]);
        setParsedFragments(parsedFragments);
    };

    fillItemType = (itemId: number, itemType: ItemTypeEntity) => {
        const { itemsData, updatedItems, setUpdatedItems, setItemsData } =
            useItemTypeStore.getState();

        const currentItem = itemsData.items.find(item => item.id === itemId);

        if (!currentItem) {
            return;
        }

        let calculatedQuantity: number;

        if (
            typeof currentItem.packaging === 'object' &&
            currentItem.packaging !== null &&
            !Array.isArray(currentItem.packaging)
        ) {
            const packagingValue = Object.values(currentItem.packaging)[0];
            if (typeof packagingValue !== 'number') {
                return;
            }
            calculatedQuantity = Math.ceil(itemType.qty / packagingValue);
        } else {
            calculatedQuantity =
                itemType.qty > Number(currentItem.qtyFromStore)
                    ? Number(currentItem.qtyFromStore)
                    : itemType.qty;
        }

        currentItem.updateQuantity(calculatedQuantity);

        const updatedItemIds = updatedItems.map(item => item.id);

        if (updatedItemIds.includes(currentItem.id)) {
            setUpdatedItems(updatedItems);
            setItemsData(itemsData);
            return;
        }

        setItemsData(itemsData);
        setUpdatedItems([...updatedItems, currentItem]);
    };

    clearItemTypeQuantity = (itemType: ItemTypeEntity) => {
        const { itemsData, setUpdatedItems, setItemsData, updatedItems } =
            useItemTypeStore.getState();

        const allItemsToClearMap = new Map();
        (itemType.items ?? []).forEach(item =>
            allItemsToClearMap.set(item.id, item)
        );
        updatedItems.forEach(item => allItemsToClearMap.set(item.id, item));

        const clearedItems = Array.from(allItemsToClearMap.values()).map(
            item => {
                const clearedItem = new ItemModel(item);
                clearedItem.updateQuantity(0);
                return clearedItem;
            }
        );
        setUpdatedItems(clearedItems);
        itemsData.items.forEach(item => {
            item.updateQuantity(0);
        });
        setItemsData({
            ...itemsData,
            items: [...itemsData.items],
        });
    };

    calculateTotalQuantityFromItemType = (itemType: ItemTypeEntity) => {
        const { updatedItems } = useItemTypeStore.getState();

        const allItemsMap = new Map();
        (itemType.items ?? []).forEach(item => {
            allItemsMap.set(item.id, item);
        });
        updatedItems.forEach(item => {
            allItemsMap.set(item.id, item);
        });
        const allItemsToCalculate = Array.from(allItemsMap.values());

        const total = allItemsToCalculate.reduce((total, item) => {
            const qty = typeof item.qty === 'number' ? item.qty : 0;
            const packaging = item.packaging;

            if (typeof packaging === 'object' && packaging !== null) {
                const packagingValue = Object.values(packaging)[0];
                const packagingKey = Object.keys(packaging)[0];
                if (
                    typeof packagingValue === 'number' &&
                    packagingKey.toLowerCase() === itemType.unit.toLowerCase()
                ) {
                    return total + qty * packagingValue;
                }
            }

            return total + qty;
        }, 0);

        return parseFloat(total.toFixed(2));
    };

    updateItemQuantity = (id: number, qty: number) => {
        const { itemsData, updatedItems, setUpdatedItems, setItemsData } =
            useItemTypeStore.getState();

        const currentItem = itemsData.items.find(i => i.id === id);
        if (!currentItem) return;

        currentItem.updateQuantity(qty);
        const updatedItemIds = updatedItems.map(i => i.id);
        let newUpdatedItems;

        if (updatedItemIds.includes(currentItem.id)) {
            newUpdatedItems = updatedItems.map(item =>
                item.id === currentItem.id ? currentItem : item
            );
        } else {
            newUpdatedItems = [...updatedItems, currentItem];
        }
        setUpdatedItems(newUpdatedItems);

        setItemsData({
            ...itemsData,
            items: [...itemsData.items],
        });
    };
}
